// src/components/admin/AdminBillingTab.js
// ============================================================================
// Billing Tab (Owner only)
// - Seat changes happen inside the app
// - Increases: prorate + invoice now
// - Decreases: prorated credit next invoice
//
// ✅ Paid seats = company.seatLimits (synced from Stripe)
// ✅ Gift seats = company.giftSeatLimits (manual; DOES NOT affect Stripe)
// ✅ Effective seats = paid + gift
// ✅ Trial defaults: admin=1, endUser=0, customer=0 (gifts can add if you set them)
// ✅ Paid plans: customers show as unlimited (∞)
// ============================================================================

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ExternalLink,
  Receipt,
  ShieldCheck,
  Loader2,
  Plus,
  Minus,
  AlertTriangle,
  X,
  Gift,
} from "lucide-react";

import { clampInt, computeDaysRemaining, formatDate, money } from "./adminBillingUtils";
import { postFunctionJson } from "../../utils/functionHttp";

const normalizeRole = (role) => {
  const r = String(role || "").trim().toLowerCase();
  if (["enduser", "end user", "end_user", "end-user"].includes(r)) return "enduser";
  if (["super_admin", "super admin", "super-admin"].includes(r)) return "admin";
  return r;
};

const normalizeStatus = (status) => String(status || "").trim().toLowerCase();

const isActiveUser = (u) => {
  const s = u?.status;
  if (!s) return true; // treat missing as active
  return normalizeStatus(s) === "active";
};

const AdminBillingTab = ({
  firebaseUser,
  userProfile,
  company,
  companyId,
  teamUsers,
  isOwner,
  setError,
  loadCompany,
}) => {
  const [billingSummary, setBillingSummary] = useState(null);

  const [seatSaving, setSeatSaving] = useState(false);
  const [seatError, setSeatError] = useState("");
  const [desiredAdminSeats, setDesiredAdminSeats] = useState(0);
  const [desiredEndUserSeats, setDesiredEndUserSeats] = useState(0);

  const [seatPreviewLoading, setSeatPreviewLoading] = useState(false);
  const [seatPreviewError, setSeatPreviewError] = useState("");
  const [seatPreview, setSeatPreview] = useState(null);

  const [billingPortalLoading, setBillingPortalLoading] = useState(false);
  const [cancelSubscriptionLoading, setCancelSubscriptionLoading] = useState(false);
  const [reactivateSubscriptionLoading, setReactivateSubscriptionLoading] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);

  const previewDebounceRef = useRef(null);
  const navigate = useNavigate();

  // -----------------------------
  // Fetch billing summary (Stripe truth)
  // -----------------------------
  useEffect(() => {
    const fetchBillingSummary = async () => {
      if (!firebaseUser) return;

      try {
        const authToken = await firebaseUser.getIdToken(true);

        const res = await postFunctionJson(
          "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/getBillingSummary",
          {
            authToken,
          }
        );

        const data = await res.json();

        if (!res.ok) {
          console.error("[AdminBillingTab] getBillingSummary error:", data);
          return;
        }

        setBillingSummary(data);
      } catch (err) {
        console.error("[AdminBillingTab] Error fetching billing summary:", err);
      }
    };

    fetchBillingSummary();
  }, [firebaseUser]);

  // -----------------------------
  // Plan / trial resolution
  // -----------------------------
  const summaryIsTrial = billingSummary?.isTrial;

  const trialEndsAtFromSummary =
    summaryIsTrial && billingSummary?.currentPeriodEnd
      ? { seconds: billingSummary.currentPeriodEnd }
      : null;

  const trialEndsAt =
    company?.trialEndsAt || userProfile?.trialEndsAt || trialEndsAtFromSummary;

  const trialDaysRemaining = computeDaysRemaining(trialEndsAt);

  const effectivePlanType =
    company?.planType || userProfile?.planType || (summaryIsTrial ? "trial" : null);

  const isTrial = summaryIsTrial ?? effectivePlanType === "trial";

  // ✅ STRICT canonical subscription id
  const subscriptionId = company?.stripeSubscriptionId || null;
  const hasPaidSubscription = !!subscriptionId && !isTrial;

  // -----------------------------
  // Seats used (normalize role/status)
  // -----------------------------
  const activeForSeat = useMemo(() => {
    const list = Array.isArray(teamUsers) ? teamUsers : [];
    return list.filter((u) => isActiveUser(u));
  }, [teamUsers]);

  const adminUsed = useMemo(() => {
    return activeForSeat.filter((u) => {
      const r = normalizeRole(u?.role);
      return r === "admin" || r === "owner";
    }).length;
  }, [activeForSeat]);

  const endUserUsed = useMemo(() => {
    return activeForSeat.filter((u) => normalizeRole(u?.role) === "enduser").length;
  }, [activeForSeat]);

  const customerUsed = useMemo(() => {
    return activeForSeat.filter((u) => normalizeRole(u?.role) === "customer").length;
  }, [activeForSeat]);

  // -----------------------------
  // Paid seats + Gift seats + Effective seats
  // -----------------------------
  const paidSeatLimits = useMemo(() => {
    const raw = billingSummary?.seatLimits || company?.seatLimits || {};
    return {
      admin: clampInt(raw.admin ?? 0, 0),
      endUser: clampInt(raw.endUser ?? 0, 0),
    };
  }, [billingSummary?.seatLimits, company?.seatLimits]);

  const giftSeatLimits = useMemo(() => {
    const raw = company?.giftSeatLimits || {};
    return {
      admin: clampInt(raw.admin ?? 0, 0),
      endUser: clampInt(raw.endUser ?? 0, 0),
    };
  }, [company?.giftSeatLimits]);

  // Trial defaults (paid base)
  const baseAdmin = isTrial ? 1 : paidSeatLimits.admin;
  const baseEndUser = isTrial ? 0 : paidSeatLimits.endUser;

  // Effective limits = base + gift
  const effectiveAdminLimit = clampInt(baseAdmin + giftSeatLimits.admin, 0);
  const effectiveEndUserLimit = clampInt(baseEndUser + giftSeatLimits.endUser, 0);

  const customerLimit = isTrial ? 0 : Infinity;

  const limitText = (v) => (v === Infinity ? "∞" : String(clampInt(v, 0)));

  // ✅ Paid seat minimums can go down only if gifts cover usage
  const minPaidAdmin = Math.max(adminUsed - giftSeatLimits.admin, 0);
  const minPaidEndUser = Math.max(endUserUsed - giftSeatLimits.endUser, 0);

  // Keep desired seats synced to PAID seats (Stripe-billed seats), not effective
  useEffect(() => {
    setDesiredAdminSeats(clampInt(paidSeatLimits.admin, 0));
    setDesiredEndUserSeats(clampInt(paidSeatLimits.endUser, 0));
    setSeatPreview(null);
    setSeatPreviewError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paidSeatLimits.admin, paidSeatLimits.endUser]);

  useEffect(() => {
    setDesiredAdminSeats((current) => Math.max(clampInt(current, 0), minPaidAdmin));
    setDesiredEndUserSeats((current) => Math.max(clampInt(current, 0), minPaidEndUser));
  }, [minPaidAdmin, minPaidEndUser]);

  const seatsChanged =
    clampInt(desiredAdminSeats, 0) !== clampInt(paidSeatLimits.admin, 0) ||
    clampInt(desiredEndUserSeats, 0) !== clampInt(paidSeatLimits.endUser, 0);

  const isIncrease =
    clampInt(desiredAdminSeats, 0) > clampInt(paidSeatLimits.admin, 0) ||
    clampInt(desiredEndUserSeats, 0) > clampInt(paidSeatLimits.endUser, 0);

  // -----------------------------
  // ✅ Plan label/status/renews: use billingSummary, then webhook snapshot fallback
  // -----------------------------
  const snapSub = company?.subscription || null;

  const planLabel =
    (isTrial && "Trial") ||
    billingSummary?.planNickname ||
    snapSub?.planNickname ||
    company?.planNickname ||
    (hasPaidSubscription ? "Paid plan" : "No plan");

  const statusLabel =
    billingSummary?.status ||
    snapSub?.status ||
    company?.subscriptionStatus ||
    (isTrial ? "trial" : null) ||
    (hasPaidSubscription ? "active" : "none");

  const currentPeriodEndLabel = formatDate(
    billingSummary?.currentPeriodEnd ||
      snapSub?.currentPeriodEnd ||
      company?.currentPeriodEnd ||
      trialEndsAt
  );

  const cancelAtPeriodEnd =
    billingSummary?.subscription?.cancelAtPeriodEnd ??
    snapSub?.cancelAtPeriodEnd ??
    company?.subscription?.cancelAtPeriodEnd ??
    false;

  const isGrandfatheredPricing = company?.grandfatheredPricing === true;

  // -----------------------------
  // Seat Preview (Stripe) – PAID seats only
  // -----------------------------
  const fetchSeatPreview = async (nextAdminPaid, nextEndUserPaid) => {
    if (!firebaseUser || !isOwner) return;
    if (!subscriptionId) return;
    if (isTrial) return;

    setSeatPreviewLoading(true);
    setSeatPreviewError("");
    setSeatPreview(null);

    try {
      const authToken = await firebaseUser.getIdToken(true);

      const res = await postFunctionJson(
        "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/previewSeatCountsChange",
        {
          authToken,
          body: {
            subscriptionId,
            adminSeats: clampInt(nextAdminPaid, 0),
            endUserSeats: clampInt(nextEndUserPaid, 0),
          },
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Preview failed.");

      setSeatPreview(data);
    } catch (err) {
      console.error("[AdminBillingTab] Seat preview error:", err);
      setSeatPreviewError(err?.message || "Could not preview invoice.");
    } finally {
      setSeatPreviewLoading(false);
    }
  };

  useEffect(() => {
    if (!hasPaidSubscription || !seatsChanged) {
      setSeatPreview(null);
      setSeatPreviewError("");
      setSeatPreviewLoading(false);
      return;
    }

    if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);

    previewDebounceRef.current = setTimeout(() => {
      fetchSeatPreview(desiredAdminSeats, desiredEndUserSeats);
    }, 350);

    return () => {
      if (previewDebounceRef.current) clearTimeout(previewDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [desiredAdminSeats, desiredEndUserSeats, subscriptionId, isTrial, isOwner, hasPaidSubscription, seatsChanged]);

  if (!isOwner) return null;

  // -----------------------------
  // Billing actions
  // -----------------------------
  const handleOpenBillingPortal = async () => {
    if (!firebaseUser || !isOwner) return;

    setBillingPortalLoading(true);
    setError?.("");

    try {
      const authToken = await firebaseUser.getIdToken(true);

      const res = await postFunctionJson(
        "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/createStripePortalSession",
        {
          authToken,
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not open billing portal.");
      if (!data.url) throw new Error("Billing portal URL was not returned.");

      window.location.href = data.url;
    } catch (err) {
      console.error("[AdminBillingTab] Error opening billing portal:", err);
      setError?.(err.message || "Could not open billing portal.");
    } finally {
      setBillingPortalLoading(false);
    }
  };

  const handleUpgradeFromTrial = () => {
    navigate("/complete-account-setup?mode=upgrade");
  };

  const handleCancelSubscription = async () => {
    if (!firebaseUser || !isOwner) return;
    if (!subscriptionId) {
      setError?.("No active subscription found to cancel.");
      return;
    }

    const confirmed = window.confirm(
      "Cancel subscription?\n\nYou will keep access until the end of the current billing period."
    );
    if (!confirmed) return;

    setCancelSubscriptionLoading(true);
    setError?.("");

    try {
      const authToken = await firebaseUser.getIdToken(true);

      const res = await postFunctionJson(
        "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/cancelSubscription",
        {
          authToken,
          body: { subscriptionId },
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not cancel subscription.");

      await loadCompany?.(companyId);
      alert("Subscription updated.");
      window.location.reload();
    } catch (err) {
      console.error("[AdminBillingTab] Error canceling subscription:", err);
      setError?.(err.message || "Could not cancel subscription.");
    } finally {
      setCancelSubscriptionLoading(false);
    }
  };

  const handleReactivateSubscription = async () => {
    if (!firebaseUser || !isOwner) return;
    if (!subscriptionId) {
      setError?.("No active subscription found to reactivate.");
      return;
    }

    const confirmed = window.confirm(
      "Reactivate subscription?\n\nThis will remove the scheduled cancellation and keep billing active."
    );
    if (!confirmed) return;

    setReactivateSubscriptionLoading(true);
    setError?.("");

    try {
      const authToken = await firebaseUser.getIdToken(true);

      const res = await postFunctionJson(
        "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/reactivateSubscription",
        {
          authToken,
          body: { subscriptionId },
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Could not reactivate subscription.");

      await loadCompany?.(companyId);
      alert("Subscription reactivated.");
      window.location.reload();
    } catch (err) {
      console.error("[AdminBillingTab] Error reactivating subscription:", err);
      setError?.(err.message || "Could not reactivate subscription.");
    } finally {
      setReactivateSubscriptionLoading(false);
    }
  };

  // -----------------------------
  // Seat Update: PAID seats only, gifts don’t bill
  // -----------------------------
  const handleUpdateSeats = async () => {
    if (!firebaseUser || !isOwner) return;

    if (!subscriptionId) {
      setSeatError("No paid subscription found. Upgrade first to change seats.");
      return;
    }
    if (isTrial) {
      setSeatError("Seat changes are billed on paid subscriptions. Upgrade from trial first.");
      return;
    }

    const newAdminPaid = clampInt(desiredAdminSeats, 0);
    const newEndUserPaid = clampInt(desiredEndUserSeats, 0);

    // ✅ Guard: PAID + GIFT must cover used
    if (newAdminPaid < minPaidAdmin) {
      setSeatError(
        `You currently have ${adminUsed} admin/owner users and ${giftSeatLimits.admin} gifted admin seats. Paid admin seats cannot be less than ${minPaidAdmin}.`
      );
      return;
    }
    if (newEndUserPaid < minPaidEndUser) {
      setSeatError(
        `You currently have ${endUserUsed} end-user users and ${giftSeatLimits.endUser} gifted end-user seats. Paid end-user seats cannot be less than ${minPaidEndUser}.`
      );
      return;
    }

    setSeatSaving(true);
    setSeatError("");
    setError?.("");

    try {
      const authToken = await firebaseUser.getIdToken(true);

      const res = await postFunctionJson(
        "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/updateSeatCounts",
        {
          authToken,
          body: {
            subscriptionId,
            adminSeats: newAdminPaid,
            endUserSeats: newEndUserPaid,
          },
        }
      );

      const data = await res.json();
      if (!res.ok || data?.success !== true) {
        throw new Error(data?.error || "Failed to update seat counts.");
      }

      // Refresh billing summary
      try {
        const newToken = await firebaseUser.getIdToken(true);
        const summaryRes = await postFunctionJson(
          "https://us-central1-deliveryapp-c1bf2.cloudfunctions.net/getBillingSummary",
          {
            authToken: newToken,
          }
        );
        const summaryData = await summaryRes.json();
        if (summaryRes.ok) setBillingSummary(summaryData);
      } catch (e) {
        console.warn("[AdminBillingTab] Could not refresh billing summary:", e);
      }

      await loadCompany?.(companyId);

      setConfirmOpen(false);

      const invoice = data?.invoice || null;
      if (invoice?.id) {
        const amountText =
          invoice?.amount_due != null
            ? money(invoice.amount_due, invoice.currency || "usd")
            : "N/A";

        const details =
          (data.proration_behavior === "always_invoice"
            ? `This change was billed immediately.\nAmount due now: ${amountText}`
            : `This change will apply as a proration/credit on your next invoice.`) +
          `\nInvoice status: ${invoice.status || "unknown"}`;

        if (invoice.hosted_invoice_url) {
          const openNow = window.confirm(
            `Seats updated.\n\n${details}\n\nOpen the invoice in a new tab?`
          );
          if (openNow) window.open(invoice.hosted_invoice_url, "_blank", "noopener,noreferrer");
        } else {
          alert(`Seats updated.\n\n${details}`);
        }
      } else {
        alert("Seats updated.");
      }

      setSeatPreview(null);
      setSeatPreviewError("");
    } catch (err) {
      console.error("[AdminBillingTab] Error updating seat counts:", err);
      setSeatError(err.message || "Could not update seat counts.");
    } finally {
      setSeatSaving(false);
    }
  };

  // Confirmation modal open
  const openConfirmModal = () => {
    setSeatError("");
    setError?.("");

    if (!hasPaidSubscription) {
      setSeatError(isTrial ? "Upgrade from trial to change seats." : "No paid subscription found.");
      return;
    }

    const newAdminPaid = clampInt(desiredAdminSeats, 0);
    const newEndUserPaid = clampInt(desiredEndUserSeats, 0);

    if (newAdminPaid < minPaidAdmin) {
      setSeatError(
        `Paid admin seats cannot be less than ${minPaidAdmin} (used=${adminUsed}, gifted=${giftSeatLimits.admin}).`
      );
      return;
    }
    if (newEndUserPaid < minPaidEndUser) {
      setSeatError(
        `Paid end-user seats cannot be less than ${minPaidEndUser} (used=${endUserUsed}, gifted=${giftSeatLimits.endUser}).`
      );
      return;
    }

    if (!seatPreview && !seatPreviewLoading && seatsChanged) {
      fetchSeatPreview(newAdminPaid, newEndUserPaid);
    }

    setConfirmOpen(true);
  };

  const previewCurrency = seatPreview?.currency || billingSummary?.currency || "usd";
  const previewProration = seatPreview?.prorationTotal ?? null;
  const previewInvoiceTotal = seatPreview?.upcomingTotal ?? null;
  const previewRecurringDelta = seatPreview?.recurringDelta ?? null;
  const previewRecurringSubtotal = seatPreview?.nextRecurringSubtotal ?? null;
  const previewPeriodEnd = seatPreview?.currentPeriodEnd
    ? formatDate(seatPreview.currentPeriodEnd)
    : currentPeriodEndLabel;
  const paidAdminDelta = clampInt(desiredAdminSeats, 0) - clampInt(paidSeatLimits.admin, 0);
  const paidEndUserDelta = clampInt(desiredEndUserSeats, 0) - clampInt(paidSeatLimits.endUser, 0);
  const currentEffectiveAdminLimit = clampInt(paidSeatLimits.admin, 0) + giftSeatLimits.admin;
  const currentEffectiveEndUserLimit = clampInt(paidSeatLimits.endUser, 0) + giftSeatLimits.endUser;

  const invoiceLink = seatPreview?.hosted_invoice_url || null;

  const SeatStepper = ({ value, setValue, minValue, disabled }) => {
    const v = clampInt(value, 0);
    const minV = clampInt(minValue, 0);

    return (
      <div className="inline-flex items-center gap-2">
        <button
          type="button"
          onClick={() => setValue(clampInt(v - 1, 0))}
          className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center disabled:opacity-50"
          disabled={disabled || v <= minV}
          title={v <= minV ? "Cannot go below minimum paid seats" : "Decrease"}
        >
          <Minus className="w-4 h-4 text-slate-700" />
        </button>

        <input
          type="number"
          min={minV}
          value={v}
          onChange={(e) => setValue(clampInt(e.target.value, minV))}
          className="w-20 text-center rounded-lg border border-slate-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-60"
          disabled={disabled}
        />

        <button
          type="button"
          onClick={() => setValue(clampInt(v + 1, 0))}
          className="w-8 h-8 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 flex items-center justify-center disabled:opacity-50"
          disabled={disabled}
          title="Increase"
        >
          <Plus className="w-4 h-4 text-slate-700" />
        </button>
      </div>
    );
  };

  const seatControlsDisabled = seatSaving || !hasPaidSubscription || cancelAtPeriodEnd;
  const nextEffectiveAdminLimit = clampInt(desiredAdminSeats, 0) + giftSeatLimits.admin;
  const nextEffectiveEndUserLimit = clampInt(desiredEndUserSeats, 0) + giftSeatLimits.endUser;

  return (
    <div className="space-y-4">
      {/* Confirmation Modal (unchanged layout; still bills paid seats only) */}
      {confirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => {
              if (!seatSaving) setConfirmOpen(false);
            }}
          />
          <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-xl border border-slate-200">
            <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-50 border border-amber-200 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-700" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-slate-900">Confirm billing change</div>
                  <div className="text-xs text-slate-600 mt-0.5">
                    {isIncrease
                      ? "This will charge immediately (proration invoice)."
                      : "Decreases typically create a credit on your next invoice."}
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="p-2 rounded-lg hover:bg-slate-50 disabled:opacity-50"
                onClick={() => setConfirmOpen(false)}
                disabled={seatSaving}
                title="Close"
              >
                <X className="w-4 h-4 text-slate-700" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="text-[11px] text-slate-500">Seat changes (paid)</div>
                <div className="mt-2 space-y-2 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">Admin seats</span>
                    <span className="font-semibold text-slate-900">
                      {paidSeatLimits.admin} → {clampInt(desiredAdminSeats, 0)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-700">End-user seats</span>
                    <span className="font-semibold text-slate-900">
                      {paidSeatLimits.endUser} → {clampInt(desiredEndUserSeats, 0)}
                    </span>
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-1 gap-2 text-[11px] text-slate-600 sm:grid-cols-2">
                  <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                    <div className="font-medium text-slate-800">Admin effective seats</div>
                    <div className="mt-0.5">
                      {currentEffectiveAdminLimit} now → {nextEffectiveAdminLimit} after save
                    </div>
                  </div>
                  <div className="rounded-lg border border-slate-200 bg-white px-2.5 py-2">
                    <div className="font-medium text-slate-800">End-user effective seats</div>
                    <div className="mt-0.5">
                      {currentEffectiveEndUserLimit} now → {nextEffectiveEndUserLimit} after save
                    </div>
                  </div>
                </div>
                {(giftSeatLimits.admin > 0 || giftSeatLimits.endUser > 0) ? (
                  <div className="mt-2 text-[11px] text-slate-600 flex items-center gap-2">
                    <Gift className="w-4 h-4 text-slate-600" />
                    Gift seats stay the same and do not affect Stripe billing totals.
                  </div>
                ) : null}
              </div>

              <div className="rounded-xl border border-slate-200 p-3">
                <div className="text-[11px] text-slate-500">Estimated impact</div>

                {seatPreviewLoading ? (
                  <div className="mt-2 text-sm text-slate-700 flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Calculating…
                  </div>
                ) : seatPreviewError ? (
                  <div className="mt-2 text-sm text-red-700">{seatPreviewError}</div>
                ) : seatPreview ? (
                  <div className="mt-2 grid grid-cols-2 gap-3">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-[11px] text-slate-500">Proration due now</div>
                      <div className="text-base font-semibold text-slate-900 mt-0.5">
                        {money(previewProration ?? 0, previewCurrency)}
                      </div>
                    </div>
                    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-[11px] text-slate-500">Monthly seat change</div>
                      <div className="text-base font-semibold text-slate-900 mt-0.5">
                        {money(previewRecurringDelta ?? 0, previewCurrency)}
                      </div>
                    </div>

                    <div className="col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                      <div className="text-[11px] text-slate-500">Next full monthly bill</div>
                      <div className="text-base font-semibold text-slate-900 mt-0.5">
                        {money(previewRecurringSubtotal ?? previewInvoiceTotal ?? 0, previewCurrency)}
                      </div>
                      <div className="mt-1 text-[11px] text-slate-500">
                        This is the ongoing billed seat subtotal after the change, separate from gift seats.
                      </div>
                    </div>

                    <div className="col-span-2 rounded-xl border border-slate-200 bg-white p-3 text-[11px] text-slate-600">
                      <div className="font-medium text-slate-800">What Stripe is previewing</div>
                      <div className="mt-1">
                        Admin billed seats: {paidSeatLimits.admin} → {clampInt(desiredAdminSeats, 0)}
                        {paidAdminDelta !== 0 ? ` (${paidAdminDelta > 0 ? "+" : ""}${paidAdminDelta})` : ""}
                      </div>
                      <div className="mt-0.5">
                        End-user billed seats: {paidSeatLimits.endUser} → {clampInt(desiredEndUserSeats, 0)}
                        {paidEndUserDelta !== 0 ? ` (${paidEndUserDelta > 0 ? "+" : ""}${paidEndUserDelta})` : ""}
                      </div>
                      {(giftSeatLimits.admin > 0 || giftSeatLimits.endUser > 0) ? (
                        <div className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-900">
                          Gift seats are separate from billing. You currently have +{giftSeatLimits.admin} gifted admin and +{giftSeatLimits.endUser} gifted end-user seats, so the effective seat total can be higher than what Stripe bills.
                        </div>
                      ) : null}
                      <div className="mt-1.5 text-slate-500">
                        `Proration due now` is the immediate charge or credit. `Monthly seat change` is the ongoing billed seat difference.
                      </div>
                    </div>

                    <div className="col-span-2 text-[11px] text-slate-500">
                      Renews: {previewPeriodEnd || "—"}
                    </div>

                    {invoiceLink ? (
                      <div className="col-span-2">
                        <a
                          href={invoiceLink}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
                        >
                          <ExternalLink className="w-4 h-4" />
                          View invoice preview
                        </a>
                      </div>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-2 text-sm text-slate-700">Preview unavailable. You can still confirm.</div>
                )}
              </div>

              {seatError ? (
                <div className="bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-xl text-sm">
                  {seatError}
                </div>
              ) : null}
            </div>

            <div className="p-4 border-t border-slate-200 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={seatSaving}
                className="px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleUpdateSeats}
                disabled={seatSaving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
              >
                {seatSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing…
                  </>
                ) : isIncrease ? (
                  "Confirm & Charge Now"
                ) : (
                  "Confirm Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Top overview */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 lg:col-span-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-blue-600" />
                <h2 className="text-base font-semibold text-slate-900">Plan Overview</h2>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Plan/status/renew date come from Stripe (with webhook snapshot fallback).
              </p>
            </div>

            {hasPaidSubscription ? (
              <button
                type="button"
                onClick={handleOpenBillingPortal}
                disabled={billingPortalLoading}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 bg-white hover:bg-slate-50 disabled:opacity-60"
              >
                <ExternalLink className="w-4 h-4" />
                {billingPortalLoading ? "Opening…" : "Open Stripe Portal"}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleUpgradeFromTrial}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium bg-blue-600 text-white hover:bg-blue-700"
              >
                <ExternalLink className="w-4 h-4" />
                {isTrial ? "Upgrade Trial" : "Choose Plan"}
              </button>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] text-slate-500">Plan</div>
              <div className="text-sm font-semibold text-slate-900 mt-0.5">{planLabel || "—"}</div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] text-slate-500">Status</div>
              <div className="text-sm font-semibold text-slate-900 mt-0.5 capitalize">
                {statusLabel || "—"}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <div className="text-[11px] text-slate-500">{isTrial ? "Trial ends" : "Renews"}</div>
              <div className="text-sm font-semibold text-slate-900 mt-0.5">
                {currentPeriodEndLabel || "—"}
              </div>
              {isTrial && typeof trialDaysRemaining === "number" ? (
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {trialDaysRemaining} day{trialDaysRemaining === 1 ? "" : "s"} remaining
                </div>
              ) : null}
              {cancelAtPeriodEnd && currentPeriodEndLabel ? (
                <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                  Subscription scheduled to cancel on <span className="font-semibold">{currentPeriodEndLabel}</span>.
                </div>
              ) : null}
            </div>
          </div>

          {isGrandfatheredPricing ? (
            <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
              This company is grandfathered as one of SupplyHawk’s first 10 customers. Their per-user pricing will not change for the lifetime of their active relationship with SupplyHawk.
            </div>
          ) : null}
        </div>

        {/* Seats Used (Used / Available) */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <div className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-semibold text-slate-900">Seats Used</h2>
          </div>
          <div className="text-[11px] text-slate-500 mt-1">Used / Available</div>

          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Admin / Owner</span>
              <span className="font-semibold text-slate-900">
                {adminUsed} / {limitText(effectiveAdminLimit)}
              </span>
            </div>

            {giftSeatLimits.admin > 0 ? (
              <div className="flex items-center justify-between text-[11px] text-slate-600">
                <span className="inline-flex items-center gap-1">
                  <Gift className="w-3.5 h-3.5" /> Gift admin seats
                </span>
                <span className="font-semibold">+{giftSeatLimits.admin}</span>
              </div>
            ) : null}

            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">End Users</span>
              <span className="font-semibold text-slate-900">
                {endUserUsed} / {limitText(effectiveEndUserLimit)}
              </span>
            </div>

            {giftSeatLimits.endUser > 0 ? (
              <div className="flex items-center justify-between text-[11px] text-slate-600">
                <span className="inline-flex items-center gap-1">
                  <Gift className="w-3.5 h-3.5" /> Gift end-user seats
                </span>
                <span className="font-semibold">+{giftSeatLimits.endUser}</span>
              </div>
            ) : null}

            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Customers</span>
              <span className="font-semibold text-slate-900">
                {customerUsed} / {limitText(customerLimit)}
              </span>
            </div>

            <div className="pt-2 mt-2 border-t border-slate-200 text-[11px] text-slate-500">
              Minimum paid seats are locked to <b>used − gifted</b>.
            </div>
            <div className="text-[11px] text-slate-500">
              Billing counts only active <span className="font-semibold text-slate-700">owners/admins</span> and active <span className="font-semibold text-slate-700">end users</span>. Customers do not count toward paid seats.
            </div>
          </div>
        </div>
      </div>

      {/* Seat Controls + Preview + Save */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Seat controls */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 lg:col-span-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-slate-900">Adjust paid seats</h3>
              <p className="text-xs text-slate-500 mt-1">
                {cancelAtPeriodEnd
                  ? "This subscription is scheduled to end, so seat billing is view-only until it is reactivated."
                  : "Gift seats are separate (no billing impact). Paid seat changes bill through Stripe."}
              </p>
            </div>

            {!hasPaidSubscription ? (
              <div className="text-[11px] px-2 py-1 rounded-lg border border-amber-200 bg-amber-50 text-amber-900">
                {isTrial ? "Trial plan" : "No paid subscription found"}
              </div>
            ) : (
              <div className="text-[11px] px-2 py-1 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900">
                Paid subscription active
              </div>
            )}
          </div>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-[11px] text-slate-500">Admin seats billed through Stripe</div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <SeatStepper
                  value={desiredAdminSeats}
                  setValue={setDesiredAdminSeats}
                  minValue={minPaidAdmin}
                  disabled={seatControlsDisabled}
                />
                <div className="text-right">
                  <div className="text-xs text-slate-500">Effective</div>
                  <div className="text-sm font-semibold text-slate-900">
                    {limitText(nextEffectiveAdminLimit)}
                  </div>
                </div>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                Current billed: {paidSeatLimits.admin} · Minimum allowed: {minPaidAdmin}
              </div>
              {giftSeatLimits.admin > 0 ? (
                <div className="mt-1 text-[11px] text-amber-700">
                  +{giftSeatLimits.admin} gifted admin seat{giftSeatLimits.admin === 1 ? "" : "s"} already available outside billing.
                </div>
              ) : null}
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <div className="text-[11px] text-slate-500">End-user seats billed through Stripe</div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <SeatStepper
                  value={desiredEndUserSeats}
                  setValue={setDesiredEndUserSeats}
                  minValue={minPaidEndUser}
                  disabled={seatControlsDisabled}
                />
                <div className="text-right">
                  <div className="text-xs text-slate-500">Effective</div>
                  <div className="text-sm font-semibold text-slate-900">
                    {limitText(nextEffectiveEndUserLimit)}
                  </div>
                </div>
              </div>
              <div className="mt-2 text-[11px] text-slate-500">
                Current billed: {paidSeatLimits.endUser} · Minimum allowed: {minPaidEndUser}
              </div>
              {giftSeatLimits.endUser > 0 ? (
                <div className="mt-1 text-[11px] text-amber-700">
                  +{giftSeatLimits.endUser} gifted end-user seat{giftSeatLimits.endUser === 1 ? "" : "s"} already available outside billing.
                </div>
              ) : null}
            </div>
          </div>

          {seatError ? (
            <div className="mt-3 bg-red-50 border border-red-200 text-red-800 px-3 py-2 rounded-xl text-sm">
              {seatError}
            </div>
          ) : null}

          <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-xs text-slate-500">
              {seatsChanged ? "Changes detected — preview will update automatically." : "No changes yet."}
            </div>

            <button
              type="button"
              onClick={openConfirmModal}
              disabled={!hasPaidSubscription || seatSaving || !seatsChanged || cancelAtPeriodEnd}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60"
              title={
                !hasPaidSubscription
                  ? "Upgrade to a paid subscription to bill seat changes"
                  : cancelAtPeriodEnd
                  ? "Reactivate the subscription to change billed seats"
                  : !seatsChanged
                  ? "Adjust seat counts first"
                  : "Review and confirm billing change"
              }
            >
              {seatSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save & Bill Now"
              )}
            </button>
          </div>

          {!hasPaidSubscription ? (
            <div className="mt-3 text-[11px] text-slate-500">
              On trial: seats are fixed until you switch to a paid plan using the main billing action above.
            </div>
          ) : cancelAtPeriodEnd ? (
            <div className="mt-3 text-[11px] text-slate-500">
              Seat billing is locked while the subscription is scheduled to cancel. Reactivate the subscription first to make billing changes.
            </div>
          ) : null}
        </div>

        {/* Preview card (unchanged) */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
          <h3 className="text-sm font-semibold text-slate-900">Invoice preview</h3>
          <p className="text-xs text-slate-500 mt-1">Stripe upcoming invoice estimate for paid seat changes.</p>

          <div className="mt-3 space-y-2">
            {!hasPaidSubscription ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                Upgrade to a paid plan to preview charges and manage billed seats. Use the main upgrade button in the plan overview card.
              </div>
            ) : cancelAtPeriodEnd ? (
              <div className="px-0.5 py-1 text-sm text-slate-600">
                This subscription is scheduled to cancel on {currentPeriodEndLabel || "the current period end"}. Seat billing preview is view-only until the subscription is reactivated.
              </div>
            ) : seatPreviewLoading ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Calculating…
              </div>
            ) : seatPreviewError ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                {seatPreviewError}
              </div>
            ) : !seatsChanged ? (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                Adjust seats to see a preview.
              </div>
            ) : seatPreview ? (
              <div className="space-y-2">
                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[11px] text-slate-500">Proration due now</div>
                  <div className="text-lg font-semibold text-slate-900 mt-0.5">
                    {money(seatPreview?.prorationTotal ?? 0, previewCurrency)}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    Renews: {previewPeriodEnd || "—"}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[11px] text-slate-500">Stripe next invoice estimate</div>
                  <div className="text-base font-semibold text-slate-900 mt-0.5">
                    {money(seatPreview?.upcomingTotal ?? 0, previewCurrency)}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[11px] text-slate-500">Monthly seat change</div>
                  <div className="text-base font-semibold text-slate-900 mt-0.5">
                    {money(seatPreview?.recurringDelta ?? 0, previewCurrency)}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div className="text-[11px] text-slate-500">Next full monthly bill</div>
                  <div className="text-base font-semibold text-slate-900 mt-0.5">
                    {money(seatPreview?.nextRecurringSubtotal ?? seatPreview?.upcomingTotal ?? 0, previewCurrency)}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-3 text-[11px] text-slate-600">
                  <div className="font-medium text-slate-800">Preview details</div>
                  <div className="mt-1">
                    Stripe is previewing billed seats only: admin {paidSeatLimits.admin} → {clampInt(desiredAdminSeats, 0)}, end users {paidSeatLimits.endUser} → {clampInt(desiredEndUserSeats, 0)}.
                  </div>
                  <div className="mt-1">
                    `Stripe next invoice estimate` is the full next invoice estimate. `Monthly seat change` is the seat-change amount only.
                  </div>
                  {(giftSeatLimits.admin > 0 || giftSeatLimits.endUser > 0) ? (
                    <div className="mt-1.5 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-amber-900">
                      Gift seats do not increase the invoice. They only increase available seats in-app.
                    </div>
                  ) : null}
                </div>

                {invoiceLink ? (
                  <a
                    href={invoiceLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 text-sm font-medium text-blue-700 hover:text-blue-800"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View invoice
                  </a>
                ) : null}
              </div>
            ) : (
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                Change seats to generate a preview.
              </div>
            )}
          </div>

          <div className="mt-4 pt-3 border-t border-slate-200 space-y-2">
            <button
              type="button"
              onClick={handleOpenBillingPortal}
              disabled={billingPortalLoading}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium disabled:opacity-60"
            >
              <ExternalLink className="w-4 h-4" />
              {billingPortalLoading ? "Opening…" : "Payment method / invoices"}
            </button>

            {cancelAtPeriodEnd ? (
              <button
                type="button"
                onClick={handleReactivateSubscription}
                disabled={reactivateSubscriptionLoading || !subscriptionId}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 text-sm font-medium text-emerald-700 disabled:opacity-60"
              >
                {reactivateSubscriptionLoading ? "Reactivating…" : "Reactivate subscription"}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleCancelSubscription}
                disabled={cancelSubscriptionLoading || !subscriptionId}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl border border-red-200 bg-red-50 hover:bg-red-100 text-sm font-medium text-red-700 disabled:opacity-60"
              >
                {cancelSubscriptionLoading ? "Canceling…" : "Cancel subscription"}
              </button>
            )}

            {cancelAtPeriodEnd ? (
              <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                Your subscription is scheduled to end at the close of the current billing period. Reactivating will remove that scheduled cancellation.
              </div>
            ) : null}

            <div className="text-[11px] text-slate-500">
              Gift seats are managed separately and do not change billing.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminBillingTab;
